import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';

import {
  Paper,
  Group,
  Box,
  Stack,
  Text,
  Title,
  Badge,
  Divider,
  SimpleGrid,
  Alert,
  LoadingOverlay,
  Button,
} from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { IconInfoCircle, IconClockStop, IconEdit } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import TerminateParkingContractModal from '../components/modal/parking-contract/TerminateParkingContractModal.tsx';
import SetupSpecifyParkingLotsModal from '../components/modal/parking-contract/SetupSpecifyParkingLotsModal.tsx';

import usePartner from '../hooks/partners/usePartner.ts';
import useParkingContract from '../hooks/parking-contracts/use-parking-contract.ts';
import useBriefParkingLots from '../hooks/partners/useBriefParkingLots.ts';
import useDownloadParkingContractAttachment from '../hooks/parking-contracts/use-download-parking-contract-attachment.ts';
import useTerminateParkingContract from '../hooks/parking-contracts/use-terminate-parking-contract.ts';
import useSetupParkingLots from '../hooks/parking-contracts/use-setup-parking-lots.ts';
import { formatDateTime2 } from '../utils/DateUtil.ts';
import { executeDownloadAttachment } from '../utils/attachment-utils.ts';

import type { ParkingLotFunctionType } from '../models/parking-contract.ts';
import type { Attachment } from '../models/attachment.ts';
import type { TerminateParkingContractFormValues } from '../components/modal/parking-contract/TerminateParkingContractModal.tsx';
import type { BriefParkingLot } from '../models/parking-contract.ts';

import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import {
  PROFILE_TYPE_LABEL,
  PARKING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
  PARKING_CONTRACT_REFUND_FEE_PROCESSING_METHOD_LABEL,
  PARKING_CONTRACT_BILLING_CYCLE_LABEL,
  PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL,
  defaultProfitSharingText,
  bankDiscountProfitSharingText,
  pbaReferralProfitSharingText,
} from '../const/labels.ts';
import { formatDate } from '../utils/DateUtil.ts';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import { notifyError } from '../utils/NotifyError.tsx';

export default function ParkingContractInspectPage() {
  // hooks
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerId: partnerId, id: id } = useParams();
  const queryClient = useQueryClient();

  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const { parkingContract, isLoading: isParkingContractLoading } =
    useParkingContract(partnerId, id);
  const { briefParkingLots, isLoading: isBriefParkingLotsLoading } =
    useBriefParkingLots(partnerId);
  const { mutate: downloadAttachment, isPending: isDownloadAttachmentPending } =
    useDownloadParkingContractAttachment();
  const { mutate: setupParingLots, isPending: isSetupParkingLotsPending } =
    useSetupParkingLots();
  const {
    mutate: terminateParkingContract,
    isPending: isTerminateParkingContractPending,
  } = useTerminateParkingContract();

  const [isPublishedNoticeClosed, setPublishedNoticeClosed] = useState(false);

  const [
    terminateModalOpened,
    { open: openTerminateModal, close: closeTerminateModal },
  ] = useDisclosure(false);

  const [
    setupParkingLotsModalOpened,
    { open: openSetupParkingLotsModal, close: closeSetupParkingLotsModal },
  ] = useDisclosure(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isPartnerLoading || isParkingContractLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // constants
  const shouldDisplayPublishedNotice: boolean =
    !!state?.published && !isPublishedNoticeClosed;
  const canDownload: boolean = parkingContract.attachments.length === 1;

  const newPagePath: string = `/partners/${partnerId}/parking-contracts/new`;

  const isTerminated: boolean =
    startOfDay(Date.now()) >
    startOfDay(Date.parse(parkingContract.activeEndAt));

  const parkingLotColumns: DataTableColumn<BriefParkingLot>[] = [
    {
      title: '場站名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
    },
  ];

  // callback functions
  function onClickTerminateButton(): void {
    openTerminateModal();
  }
  function onTerminate(formValues: TerminateParkingContractFormValues): void {
    if (isTerminateParkingContractPending) return;

    terminateParkingContract(
      { partnerId, id, ...formValues },
      {
        onSuccess: () => {
          closeTerminateModal();
          notifySuccess('提前終止合約成功');
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'parkingContract',
              parkingContract.id,
            ],
          });
        },
        onError: () => {
          closeTerminateModal();
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'parkingContract',
              parkingContract.id,
            ],
          });
        },
      },
    );
  }
  function onDownloadAttachment(): void {
    if (isDownloadAttachmentPending) return;

    const attachment: Attachment = parkingContract.attachments[0];
    const request = {
      partnerId: partnerId,
      contractId: parkingContract.id,
      attachmentId: attachment.id,
    };

    downloadAttachment(request, {
      onSuccess: (downloadUrl) => {
        executeDownloadAttachment(downloadUrl, attachment.displayName);
      },
    });
  }
  function onDuplicate(): void {
    navigate(newPagePath, { state: { cloneId: parkingContract.id } });
  }
  function onClickSetupSpecifyParkingLotsButton() {
    openSetupParkingLotsModal();
  }

  function handleSetupParkingLotsConfirm(formValues) {
    setupParingLots(
      { partnerId, id, ...formValues },
      {
        onSuccess: () => {
          closeSetupParkingLotsModal();
          notifySuccess('更新指定場站成功');
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'parkingContract',
              parkingContract.id,
            ],
          });
        },
        onError: (error) => {
          if (error.code === 'B0003') {
            notifyError(
              '新增指定場站失敗',
              '出現指定場站重複，請檢查其他停車場合約',
            );
          } else {
            notifyError('新增指定場站失敗', error.message);
          }
        },
      },
    );
  }

  return (
    <>
      {terminateModalOpened && (
        <TerminateParkingContractModal
          opened={terminateModalOpened}
          onClose={closeTerminateModal}
          contractEndAt={parkingContract.activeEndAt}
          isPending={isTerminateParkingContractPending}
          onConfirm={onTerminate}
        />
      )}

      {setupParkingLotsModalOpened && (
        <SetupSpecifyParkingLotsModal
          opened={setupParkingLotsModalOpened}
          onClose={closeSetupParkingLotsModal}
          initialValues={{
            activeStartAt: parkingContract.activeStartAt,
            activeEndAt: parkingContract.activeEndAt,
            parkingLotIds: parkingContract.specifyParkingLots.map(
              (pklot) => pklot.id,
            ),
          }}
          parkingLotData={briefParkingLots}
          isPending={isSetupParkingLotsPending}
          onConfirm={handleSetupParkingLotsConfirm}
          allowEditTime={false}
          allowBlankParkingLot={false}
        />
      )}

      {/* Main Content */}
      <PreviousPageButton
        href={`/partners/${partnerId}`}
        content={'返回業者詳情頁'}
      />
      <Stack gap="lg" mb={70}>
        {shouldDisplayPublishedNotice && (
          <Alert
            variant="light"
            color="green"
            withCloseButton
            icon={<IconInfoCircle />}
            onClose={() => setPublishedNoticeClosed(true)}
            title="成功發佈合約"
          />
        )}

        {!!parkingContract.terminateDate && (
          <Alert
            variant="light"
            color="blue"
            icon={<IconClockStop />}
            title={`合約將於 ${formatDate(parkingContract.terminateDate)} 終止`}
          />
        )}

        <Paper radius="lg" p="xl" shadow="xs">
          <Group justify="space-between" align="center">
            <div>
              <Title order={3}>{parkingContract.name}</Title>
              <Text fz={14} c="gray">
                {`${partner.name} | 停車場合約`}
              </Text>
            </div>
            {isTerminated ? (
              <Badge color="gray">
                <Text size="xs" fw={700}>
                  合約已終止
                </Text>
              </Badge>
            ) : (
              <Badge>
                <Text size="xs" fw={700}>
                  合約已發佈
                </Text>
              </Badge>
            )}
          </Group>
        </Paper>

        <Paper radius="lg" p="xl" shadow="xs">
          <Text fw={500} fz={14}>
            基本資訊
          </Text>
          <Divider mt={10} mb={20} />
          <SimpleGrid cols={3} verticalSpacing="xl">
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                合約套用方式
              </Text>
              <Text fz={18} fw={400}>
                {PROFILE_TYPE_LABEL[parkingContract.profileType]}
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                出帳週期
              </Text>
              <Text fz={18} fw={400}>
                {
                  PARKING_CONTRACT_BILLING_CYCLE_LABEL[
                    parkingContract.billingCycle
                  ]
                }
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                合約生效起迄日
              </Text>
              <Group>
                <Text fz={18} fw={400} w={150}>
                  {formatDateTime2(parkingContract.activeStartAt)}
                </Text>
                <Text>-</Text>
                <Text fz={18} fw={400} w={150}>
                  {formatDateTime2(parkingContract.activeEndAt)}
                </Text>
              </Group>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                手續費扣除方式
              </Text>
              <Text fz={18} fw={400}>
                {
                  PARKING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL[
                    parkingContract.disbursementFeeProcessingMethod
                  ]
                }
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                退款手續費處理方式
              </Text>
              <Text fz={18} fw={400}>
                {
                  PARKING_CONTRACT_REFUND_FEE_PROCESSING_METHOD_LABEL[
                    parkingContract.refundFeeProcessingMethod
                  ]
                }
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                紙本合約生效起迄日
              </Text>
              <Group>
                <Text fz={18} fw={400} w={150}>
                  {formatDateTime2(parkingContract.paperContractStartAt)}
                </Text>
                <Text>-</Text>
                <Text fz={18} fw={400} w={150}>
                  {formatDateTime2(parkingContract.paperContractEndAt)}
                </Text>
              </Group>
            </Box>
          </SimpleGrid>
        </Paper>

        {['ALPR', 'KIOSK', 'PAY_TAIPEI'].map(
          (parkingLotFunctionType: ParkingLotFunctionType) => {
            if (
              !parkingContract.parkingLotFunctionTypes.includes(
                parkingLotFunctionType,
              )
            ) {
              return null;
            }

            return (
              <Paper radius="lg" p="xl" shadow="xs">
                <Text fw={500} fz={14}>
                  {`${PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL[parkingLotFunctionType]}分潤資訊`}
                </Text>
                <Divider mt={10} mb={20} />
                <SimpleGrid cols={3} verticalSpacing="xl">
                  <Box>
                    <Text c="gray" fz={14} fw={500} pb={10}>
                      預設分潤條件
                    </Text>
                    <Text fz={18} fw={400}>
                      {defaultProfitSharingText(
                        parkingContract.profitSharingConfigs[
                          parkingLotFunctionType
                        ],
                      )}
                    </Text>
                  </Box>
                  <Box>
                    <Text c="gray" fz={14} fw={500} pb={10}>
                      有銀行折抵時分潤條件
                    </Text>
                    <Text fz={18} fw={400}>
                      {bankDiscountProfitSharingText(
                        parkingContract.profitSharingConfigs[
                          parkingLotFunctionType
                        ],
                      )}
                    </Text>
                  </Box>
                  <Box>
                    <Text c="gray" fz={14} fw={500} pb={10}>
                      有 PBA 導流時分潤條件
                    </Text>
                    <Text fz={18} fw={400}>
                      {pbaReferralProfitSharingText(
                        parkingContract.profitSharingConfigs[
                          parkingLotFunctionType
                        ],
                      )}
                    </Text>
                  </Box>
                  <Box>
                    <Text c="gray" fz={14} fw={500} pb={10}>
                      是否允許撥款金額超過原始請款金額
                    </Text>
                    <Text fz={18} fw={400}>
                      {parkingContract.profitSharingConfigs[
                        parkingLotFunctionType
                      ].allowDisbursementAmountOverLimit
                        ? '是'
                        : '否'}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Paper>
            );
          },
        )}

        {parkingContract.profileType === 'SPECIFY' && (
          <Paper radius="lg" p="xl" shadow="xs">
            <Group justify="space-between">
              <Title order={3}>合約指定場站</Title>
              <Button
                leftSection={<IconEdit size={20} />}
                variant="outline"
                onClick={onClickSetupSpecifyParkingLotsButton}
                disabled={parkingContract.profileType !== 'SPECIFY'}
                fw={400}
              >
                編輯
              </Button>
            </Group>
            <DataTable
              idAccessor="id"
              highlightOnHover
              minHeight={150}
              columns={parkingLotColumns}
              records={parkingContract.specifyParkingLots}
              verticalSpacing="sm"
              noRecordsText="目前還沒有任何指定場站，點擊「編輯」開始建立！"
              fetching={isBriefParkingLotsLoading}
            />
          </Paper>
        )}
      </Stack>

      {/* Footer */}
      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            onClick={onClickTerminateButton}
            disabled={
              !!parkingContract.terminateDate ||
              new Date(parkingContract.activeEndAt) < new Date()
            }
            color="red"
            fw={400}
          >
            終止合約
          </Button>
          <Button
            variant="outline"
            fw={400}
            onClick={onDownloadAttachment}
            disabled={!canDownload}
          >
            下載紙本合約
          </Button>
          <Button variant="outline" fw={400} onClick={onDuplicate}>
            複製合約
          </Button>
        </Group>
      </Box>
    </>
  );
}
